import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, registerConfigApi, type RegisterConfigKind } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';

type Department = {
  departmentId: string;
  name: string;
};

type RegisterLike = {
  registerId: string;
  name?: string;
  description?: string;
  template?: string;
  status?: string;
  departmentId?: string;
  departmentName?: string;
  createdAt?: string;
  [key: string]: any;
};

type RegisterAdminApi = {
  getAll: () => Promise<any[]>;
  create: (data: any) => Promise<any>;
  update: (registerId: string, data: any) => Promise<any>;
  delete: (registerId: string, force?: boolean) => Promise<any>;
};

type TemplateOption = { value: string; label: string };

type Props = {
  title: string;
  iconClass: string;
  queryKey: string;
  api: RegisterAdminApi;

  includeDepartments?: boolean;
  templates: TemplateOption[];
  defaultTemplate: string;
  allowBlankTemplate?: boolean;

  statusOptions?: Array<{ value: string; label: string }>;
  defaultStatus?: string;

  listPath: string;
  registerQueryParam?: string;
  viewButtonLabel?: string;
  configPath?: string;
  rowLinks?: Array<{
    label: string;
    iconClass?: string;
    to: (register: RegisterLike) => string;
  }>;
  applyTemplateKind?: RegisterConfigKind;
  applyTemplateOnCreate?: boolean;
  countField?: string;
  countLabel?: string;
  allowForceDelete?: boolean;
  /** Overrides countField when the register record itself doesn't reliably carry its own
   *  count (e.g. document registers, where nothing increments a documentCount field anywhere -
   *  confirmed live as a real bug, always reading 0). When provided, this is used instead of
   *  reading r[countField] directly, for both the displayed count and the delete-confirmation's
   *  own cascade warning. */
  computeCount?: (register: RegisterLike) => number;
};

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const RegisterManagementPage: React.FC<Props> = ({
  title,
  iconClass,
  queryKey,
  api,
  includeDepartments = true,
  templates: _templates,
  defaultTemplate,
  allowBlankTemplate = true,
  statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' },
  ],
  defaultStatus = 'active',
  listPath,
  registerQueryParam = 'registerId',
  viewButtonLabel = 'View',
  configPath,
  rowLinks: _rowLinks,
  applyTemplateKind,
  applyTemplateOnCreate = true,
  countField,
  countLabel = 'Items',
  allowForceDelete = false,
  computeCount,
}) => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: registers = [], isLoading, error } = useQuery<RegisterLike[]>({
    queryKey: [queryKey],
    queryFn: api.getAll,
  });

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
    enabled: includeDepartments,
  });

  const departmentNameById = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((d) => map.set(d.departmentId, d.name));
    return map;
  }, [departments]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    departmentId: '',
    template: defaultTemplate,
    status: defaultStatus,
  });

  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      departmentId: '',
      template: defaultTemplate,
      status: defaultStatus,
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const departmentName = includeDepartments && form.departmentId
        ? (departmentNameById.get(form.departmentId) || '')
        : '';

      const payload: any = {
        name: form.name,
        description: form.description,
        ...(includeDepartments
          ? {
              departmentId: form.departmentId || '',
              departmentName,
            }
          : {}),
        template: form.template,
        status: form.status,
      };

      if (editingId) {
        await api.update(editingId, payload);
        return { registerId: editingId, created: false };
      }

      const created = await api.create(payload);
      const registerId = created?.registerId || created?.item?.registerId || created?.register?.registerId;
      if (!registerId) {
        throw new Error('Register created but no registerId returned');
      }

      const shouldApplyTemplate =
        applyTemplateOnCreate &&
        !!applyTemplateKind &&
        !!form.template &&
        (allowBlankTemplate ? form.template !== 'blank' : true);

      if (shouldApplyTemplate) {
        try {
          await registerConfigApi.applyTemplate(applyTemplateKind, registerId, form.template);
        } catch (error) {
          console.error('Template application failed:', error);
          // Template application failure should not block creation, but log it
        }
      }

      return { registerId, created: true };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [queryKey] });
      resetForm();
      setShowModal(false); // Close modal after successful save
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.error || e?.message || 'Unknown error';
      console.error('Register save failed:', e);
      toast.error(`Failed to save register: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ r, force }: { r: RegisterLike; force: boolean }) => {
      await api.delete(r.registerId, force);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Register deleted.');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.error || e?.message || 'Unknown error';
      toast.error(`Failed to delete: ${msg}`);
    },
  });

  const handleDeleteRegister = async (r: RegisterLike) => {
    const label = r.name || r.registerId;
    const entryCount = computeCount ? computeCount(r) : (countField ? (r[countField] as number | undefined) ?? 0 : 0);

    if (allowForceDelete && entryCount > 0) {
      // Strong warning when there are entries to cascade-delete
      const result = await (window as any).Swal?.fire({
        title: 'Delete register and all contents?',
        html: `<p>You are about to permanently delete <strong>"${label}"</strong> and all <strong>${entryCount} ${countLabel.toLowerCase()}</strong> inside it.</p><p class="text-danger mt-2"><strong>This cannot be undone.</strong></p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Delete register + ${entryCount} ${countLabel}`,
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'np-btn np-btn-danger me-2',
          cancelButton: 'np-btn np-btn-ghost',
        },
        buttonsStyling: false,
      });
      if (!result?.isConfirmed) return;
    } else {
      const ok = await swal.confirmDelete(label);
      if (!ok) return;
    }
    // When allowForceDelete is enabled always pass force=true so a stale frontend
    // count of 0 cannot result in a backend 400 if items actually exist.
    deleteMutation.mutate({ r, force: allowForceDelete });
  };

  const beginEdit = (r: RegisterLike) => {
    setEditingId(r.registerId);
    setForm({
      name: r.name || '',
      description: r.description || '',
      departmentId: r.departmentId || '',
      template: r.template || defaultTemplate,
      status: r.status || defaultStatus,
    });
  };

  const [showModal, setShowModal] = useState(false);

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (r: RegisterLike) => {
    beginEdit(r);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon={iconClass}
        title={title}
        actions={
          <>
            <button className="np-btn np-btn-primary" onClick={openCreateModal}>
              <i className="ri-add-line"></i>
              Create Register
            </button>
            <Link className="np-btn np-btn-ghost" to={listPath}>
              <i className="ri-arrow-left-line"></i>
              Back
            </Link>
          </>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load registers.</div>
          ) : registers.length === 0 ? (
            <div className="np-sub">No registers found.</div>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Name</th>
                    {includeDepartments && <th style={{ width: '220px' }}>Department</th>}
                    <th style={{ width: '180px' }}>Template</th>
                    {countField && <th style={{ width: '140px' }}>{countLabel}</th>}
                    <th style={{ width: '140px' }}>Status</th>
                    <th style={{ width: '160px' }}>Created</th>
                    <th style={{ width: '260px' }}></th>
                  </tr>}>
                  {registers.map((r) => {
                    const count = computeCount ? computeCount(r) : (countField ? Number(r[countField] || 0) : 0);
                    return (
                      <tr key={r.registerId}>
                        <td>
                          <div>
                            <span className="np-row-title">{r.name || r.registerId}</span>
                            {r.description ? (
                              <div className="np-sub np-mb-0">{r.description}</div>
                            ) : null}
                          </div>
                        </td>
                        {includeDepartments && <td>{r.departmentName || <span style={{ color: 'var(--bs-secondary-color)' }}>All</span>}</td>}
                        <td>
                          <span className="np-chip np-tone-info">{r.template || defaultTemplate}</span>
                        </td>
                        {countField && (
                          <td>
                            <span className="np-chip np-tone-primary np-tabular">{Number.isFinite(count) ? count : 0}</span>
                          </td>
                        )}
                        <td>
                          <span className={`np-chip np-tone-${(r.status || defaultStatus) === 'active' ? 'success' : 'neutral'}`}>
                            {r.status || defaultStatus}
                          </span>
                        </td>
                        <td className="np-tabular">{formatDate(r.createdAt)}</td>
                        <td className="text-end">
                          <div className="d-flex gap-1 justify-content-end">
                            <Link
                              className="np-icon-btn"
                              to={`${listPath}?${registerQueryParam}=${encodeURIComponent(r.registerId)}`}
                              title={viewButtonLabel}
                            >
                              <i className="ri-eye-line"></i>
                            </Link>

                            {configPath ? (
                              <Link
                                className="np-icon-btn"
                                to={`${configPath}?registerId=${encodeURIComponent(r.registerId)}`}
                                title="Configure"
                              >
                                <i className="ri-settings-3-line"></i>
                              </Link>
                            ) : null}

                            <button
                              className="np-icon-btn"
                              onClick={() => openEditModal(r)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => handleDeleteRegister(r)}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
        </div>
      </div>

      {/* Register Modal */}
      {showModal && (
        <NimbusModal scrollable>
          <NimbusModalContent
            title={editingId ? 'Edit Register' : 'Create Register'}
            onClose={closeModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Register'}
                </button>
              </>
            }
          >
            <div className="np-field">
              <label>Register Name <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
              <input
                className="np-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={`e.g. ${title} Register`}
              />
            </div>

            <div className="np-field">
              <label>Description</label>
              <textarea
                className="np-textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Describe the purpose of this register"
              />
            </div>

            {includeDepartments && (
              <div className="np-field">
                <label>Department</label>
                <select
                  className="np-select"
                  value={form.departmentId}
                  onChange={(e) => setForm((f) => ({ ...f, departmentId: e.target.value }))}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.departmentId} value={d.departmentId}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <p className="np-hint">Optional: Assign this register to a specific department</p>
              </div>
            )}

            <div className="np-field">
              <label>Status</label>
              <select
                className="np-select"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {saveMutation.isError && (
              <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to save register.</div>
            )}
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
